import { useState, useEffect } from "react";
import axios from "axios";
import { 
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, 
  CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import { BarChart3, Globe, Mail, CreditCard, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS_COUNTRY = [
  '#E31837', '#3B82F6', '#2B648F', '#F59E0B', '#8B5CF6',
  '#EC4899', '#06B6D4', '#F97316', '#7CB1CF', '#6366F1',
  '#EF4444', '#4F8DB8', '#0EA5E9', '#D946EF', '#1B3E5A',
  '#FBBF24', '#A855F7', '#FB923C'
];

const COLORS_EMAIL = {
  pending: '#6B7280', sent: '#3B82F6', delivered: '#4F8DB8',
  read: '#2B648F', failed: '#EF4444'
};

const COLORS_CARD = {
  not_created: '#6B7280', created: '#3B82F6', added_to_wallet: '#2B648F'
};

const EMAIL_LABELS = {
  pending: 'In attesa', sent: 'Inviate', delivered: 'Consegnate', 
  read: 'Lette', failed: 'Fallite'
};

const CARD_LABELS = {
  not_created: 'Non create', created: 'Create', added_to_wallet: 'Nel Wallet'
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#1A1A1A] border border-gray-700 rounded-lg px-3 py-2 shadow-xl">
        <p className="text-white text-sm font-medium">{payload[0].name || payload[0].payload?.country || payload[0].payload?.date}</p>
        <p className="text-lojack-red text-sm font-bold">{payload[0].value}</p>
      </div>
    );
  }
  return null;
};

const RateCard = ({ label, value, color }) => (
  <div className="bg-gray-800/50 rounded-xl p-4 text-center">
    <p className="text-3xl font-bold" style={{ color }}>{value}%</p>
    <p className="text-gray-400 text-xs mt-1">{label}</p>
  </div>
);

export const AnalyticsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/dashboard/analytics`)
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) return null;

  const countryData = data.country_distribution.map(d => ({
    ...d, name: d.country
  }));

  const emailData = data.email_distribution.map(d => ({
    ...d, name: EMAIL_LABELS[d.status] || d.status,
    fill: COLORS_EMAIL[d.status] || '#6B7280'
  }));

  const cardData = data.card_distribution.map(d => ({
    ...d, name: CARD_LABELS[d.status] || d.status,
    fill: COLORS_CARD[d.status] || '#6B7280'
  }));

  return (
    <div className="space-y-6 mt-8" data-testid="analytics-dashboard">
      <h3 className="text-xl font-semibold text-white flex items-center gap-2">
        <BarChart3 className="text-lojack-red" size={22} />
        Analytics Avanzate
      </h3>

      {/* Conversion Rates */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <RateCard label="Termini Accettati" value={data.conversion_rates.terms_rate} color="#2B648F" />
        <RateCard label="Contratti Firmati" value={data.conversion_rates.contract_rate} color="#8B5CF6" />
        <RateCard label="Email Lette" value={data.conversion_rates.email_read_rate} color="#3B82F6" />
        <RateCard label="Card nel Wallet" value={data.conversion_rates.wallet_rate} color="#E31837" />
      </div>

      {/* Charts Row 1: Country + Daily Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Country Distribution */}
        <div className="wallet-card">
          <h4 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
            <Globe className="text-lojack-red" size={18} />
            Distribuzione per Paese
          </h4>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={countryData}
                cx="50%"
                cy="50%"
                outerRadius={90}
                innerRadius={45}
                dataKey="count"
                nameKey="name"
                paddingAngle={2}
                stroke="none"
              >
                {countryData.map((_, i) => (
                  <Cell key={i} fill={COLORS_COUNTRY[i % COLORS_COUNTRY.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ fontSize: '11px', color: '#9CA3AF' }}
                iconSize={8}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Daily Trend */}
        <div className="wallet-card">
          <h4 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
            <BarChart3 className="text-lojack-red" size={18} />
            Clienti - Ultimi 14 Giorni
          </h4>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.daily_trend} barSize={20}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" />
              <XAxis dataKey="date" tick={{ fill: '#6B7280', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#6B7280', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" fill="#E31837" radius={[4, 4, 0, 0]} name="Clienti" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Row 2: Email + Card Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Email Distribution */}
        <div className="wallet-card">
          <h4 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
            <Mail className="text-lojack-red" size={18} />
            Distribuzione Email
          </h4>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={emailData}
                cx="50%"
                cy="50%"
                outerRadius={80}
                innerRadius={40}
                dataKey="count"
                nameKey="name"
                paddingAngle={2}
                stroke="none"
              >
                {emailData.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '11px', color: '#9CA3AF' }} iconSize={8} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Card Distribution */}
        <div className="wallet-card">
          <h4 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
            <CreditCard className="text-lojack-red" size={18} />
            Distribuzione Card
          </h4>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={cardData}
                cx="50%"
                cy="50%"
                outerRadius={80}
                innerRadius={40}
                dataKey="count"
                nameKey="name"
                paddingAngle={2}
                stroke="none"
              >
                {cardData.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '11px', color: '#9CA3AF' }} iconSize={8} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
